/**
 * Dependency-free map geometry (Track A — workforce map). No map library: the
 * admin renders a lightweight SVG over lat/lng via an equirectangular projection
 * (adequate for a city-scale workforce view; we are not doing geodesy). Pure,
 * DOM-free, unit-tested.
 */
const METERS_PER_DEG_LAT = 111_320;
const MIN_COS_LAT = 0.2;
function cosMidLat(bounds) {
    const midLat = (bounds.minLat + bounds.maxLat) / 2;
    return Math.max(MIN_COS_LAT, Math.cos((midLat * Math.PI) / 180));
}
/** Metres-per-degree-longitude at the given latitude. */
export function metersPerDegLng(latitude) {
    return METERS_PER_DEG_LAT * Math.max(MIN_COS_LAT, Math.cos((latitude * Math.PI) / 180));
}
/**
 * The bounding box of a set of points, padded by `padMeters` on every side.
 * A single point yields a small but non-degenerate box so the map still renders.
 */
export function fitBounds(points, padMeters = 1200) {
    if (points.length === 0) {
        const half = 0.005;
        return { minLat: -half, maxLat: half, minLng: -half, maxLng: half };
    }
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    for (const p of points) {
        if (p.latitude < minLat)
            minLat = p.latitude;
        if (p.latitude > maxLat)
            maxLat = p.latitude;
        if (p.longitude < minLng)
            minLng = p.longitude;
        if (p.longitude > maxLng)
            maxLng = p.longitude;
    }
    if (minLat === maxLat) {
        minLat -= 0.001;
        maxLat += 0.001;
    }
    if (minLng === maxLng) {
        minLng -= 0.001;
        maxLng += 0.001;
    }
    const latPad = padMeters / METERS_PER_DEG_LAT;
    const lngPad = padMeters / metersPerDegLng((minLat + maxLat) / 2);
    return {
        minLat: minLat - latPad,
        maxLat: maxLat + latPad,
        minLng: minLng - lngPad,
        maxLng: maxLng + lngPad,
    };
}
/**
 * Builds a projection for the given bounds + pixel canvas, preserving aspect
 * ratio (so a circle in real metres stays a circle on screen). The projected
 * region is centred inside the padded canvas.
 */
export function createProjection(bounds, width, height, padPx = 16) {
    const dLat = Math.max(bounds.maxLat - bounds.minLat, 1e-6);
    const dLng = Math.max(bounds.maxLng - bounds.minLng, 1e-6);
    const cosLat = cosMidLat(bounds);
    const aw = Math.max(1, width - padPx * 2);
    const ah = Math.max(1, height - padPx * 2);
    const k = Math.min(ah / dLat, aw / (dLng * cosLat));
    const offsetX = padPx + (aw - dLng * cosLat * k) / 2;
    const offsetY = padPx + (ah - dLat * k) / 2;
    const project = (p) => ({
        x: offsetX + (p.longitude - bounds.minLng) * cosLat * k,
        y: offsetY + (bounds.maxLat - p.latitude) * k,
    });
    return { project, metersPerPixel: METERS_PER_DEG_LAT / k };
}
/**
 * Grid clustering: snap each point to a lat/lng cell of ~`cellMeters`, group by
 * cell, and place the cluster at the centroid. "Cluster where reasonable" — a
 * handful of workers at one site collapse to a single counted marker instead of
 * overlapping dots.
 */
export function gridCluster(items, cellMeters = 150) {
    const cells = new Map();
    for (const item of items) {
        const latStep = cellMeters / METERS_PER_DEG_LAT;
        const lngStep = cellMeters / metersPerDegLng(item.latitude);
        const cx = Math.round(item.latitude / latStep);
        const cy = Math.round(item.longitude / lngStep);
        const key = `${cx}:${cy}`;
        const bucket = cells.get(key);
        if (bucket)
            bucket.push(item);
        else
            cells.set(key, [item]);
    }
    const out = [];
    for (const bucket of cells.values()) {
        let lat = 0;
        let lng = 0;
        for (const p of bucket) {
            lat += p.latitude;
            lng += p.longitude;
        }
        out.push({
            latitude: lat / bucket.length,
            longitude: lng / bucket.length,
            count: bucket.length,
            items: bucket,
        });
    }
    return out;
}
/** Whole-metre or kilometre distance, for "distance traveled". */
export function formatMeters(meters) {
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(1)} km`;
    }
    return `${Math.round(meters)} m`;
}
/** Compact duration for a session/stop (e.g. `2h 05m`, `45m`). */
export function formatMinutes(minutes) {
    const m = Math.max(0, Math.round(minutes));
    const h = Math.floor(m / 60);
    const rem = m % 60;
    if (h > 0)
        return `${h}h ${String(rem).padStart(2, '0')}m`;
    return `${rem}m`;
}
//# sourceMappingURL=mapGeometry.js.map